// src/views/bots/hooks/useBotForm.ts
// 🧠 BOT FORM LOGIC - Enhanced for Interactive Questions
// ✅ UPDATED: Removed placeholder, maxLength, min, max, helpText, countryCode fields

import { useState, useEffect, useCallback } from 'react';
import { getBotById, createBot, updateBot } from '../../../services/botService';
import { getTemplateById } from '../../../services/templates';
import toast from 'react-hot-toast';
import logger from '../../../utils/logger';

// 🎯 CLEANED INTERFACES: Removed optional configuration fields
export interface QuestionItem {
  id: string;
  question: string;
  type: 'text' | 'email' | 'phone' | 'number' | 'interactive-list' | 'interactive-button' | 'location' | 'template';
  required: boolean;
  order: number;
  // 🔥 TEMPLATE DATA: Store template information for template type questions
  templateData?: {
    templateId: string;
    templateName: string;
    template?: any; // Full template object for preview
  };
  // 🔥 INTERACTIVE: Interactive question data (preserved)
  interactive?: {
    type: 'list' | 'button';
    header?: { type: 'text'; text: string };
    body?: { text: string };
    action?: {
      button?: string;
      buttons?: Array<{
        type: string;
        reply: {
          id: string;
          title: string;
        };
      }>;
      sections?: Array<{
        title: string;
        rows: Array<{
          id: string;
          title: string;
          description?: string;
        }>;
      }>;
    };
  };
}

interface ActionData {
  type: string;
  data: any;
}

interface FormData {
  name: string;
  description: string;
  keywords: string[];
  selectedTemplate: any;
  isActive: boolean;
  responseType: 'template' | 'questionnaire';
  questions: QuestionItem[];
  botType: 'lead_generation' | 'any';
  dayActions: Record<string, ActionData[]>;
  dayDelays: Record<string, number>;
}

interface UseBotFormProps {
  isEdit?: boolean;
  botId?: string;
  onSuccess?: () => void;
  onFormStateChange?: (isValid: boolean, isSaving: boolean) => void;
}

// 🔥 HARDCODED CRM FIELD MAPPINGS: Map common question types to specific CRM fields
// This ensures that standard fields like name, phone, email automatically map to the correct lead fields
const getHardcodedCrmField = (questionType: string, questionId: string): string | null => {
  switch (questionType) {
    case 'text':
      // Check if this looks like a name field
      if (questionId.toLowerCase().includes('name') || 
          questionId === 'name' || 
          questionId.toLowerCase().includes('full_name') ||
          questionId.toLowerCase().includes('firstname') ||
          questionId.toLowerCase().includes('lastname')) {
        return 'name';
      }
      return null; // Don't map other text fields
    
    case 'phone':
      // Phone questions map to the lead's phone field
      // Backend should handle existing phone numbers for leads
      return 'phone';
    
    case 'email':
      // Email questions always map to the lead's email field
      return 'email';
    
    default:
      // Don't map other question types to CRM fields
      return null;
  }
};

// 🧠 MAIN HOOK: Enhanced Bot Form Logic
export const useBotForm = ({ isEdit = false, botId, onSuccess, onFormStateChange }: UseBotFormProps) => {
  
  // 🎯 FORM STATE
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    keywords: [],
    selectedTemplate: null,
    isActive: true,
    responseType: 'questionnaire',
    questions: [],
    botType: 'lead_generation',
    dayActions: { day_1: [] },
    dayDelays: { day_1: 1 }
  });

  // 🔑 PRESERVE IDS: Store original bot structure IDs to prevent flow breakage
  const [originalBotStructure, setOriginalBotStructure] = useState<{
    groupId?: string;
    templateStepId?: string;
  }>({});

  // 🎯 UI STATE
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadedBotId, setLoadedBotId] = useState<string | null>(null);

  // 🔥 FORM VALIDATION: Enhanced with interactive question support
  const isFormValid = useCallback(() => {
    logger.log('🔍 useBotForm: Validating form...', {
      name: formData.name,
      keywordsCount: formData.keywords.length,
      template: !!formData.selectedTemplate,
      responseType: formData.responseType,
      questionsCount: formData.questions.length,
      templateQuestions: formData.questions.filter(q => q.type === 'template').map(q => ({
        id: q.id,
        hasTemplateData: !!q.templateData,
        templateId: q.templateData?.templateId
      }))
    });

    if (!formData.name.trim()) {
      logger.log('❌ useBotForm: Validation failed - no name');
      return false;
    }
    if (formData.keywords.length === 0) {
      logger.log('❌ useBotForm: Validation failed - no keywords');
      return false;
    }
    // 🔥 REMOVED: Template is now optional since we support multiple question types
    
    // 🔥 ENHANCED: Questionnaire mode validation with interactive support
    if (formData.responseType === 'questionnaire') {
      if (formData.questions.length === 0) {
        logger.log('❌ useBotForm: Validation failed - no questions in questionnaire mode');
        return false;
      }
      
      // Check all questions have content (except template and interactive questions which don't need question text)
      const invalidQuestions = formData.questions.filter(q => 
        !['template', 'interactive-list', 'interactive-button'].includes(q.type) && !q.question.trim()
      );
      if (invalidQuestions.length > 0) {
        logger.log('❌ useBotForm: Validation failed - empty questions:', invalidQuestions.map(q => q.id));
        return false;
      }
      
      // 🔥 TEMPLATE QUESTIONS: Validate template questions have templateData
      const invalidTemplateQuestions = formData.questions.filter(q => 
        q.type === 'template' && (!q.templateData || !q.templateData.templateId)
      );
      if (invalidTemplateQuestions.length > 0) {
        logger.log('❌ useBotForm: Validation failed - template questions without templateData:', 
          invalidTemplateQuestions.map(q => q.id));
        return false;
      }
      
      // 🔥 INTERACTIVE: Validate interactive questions have required data with proper null checking
      const invalidInteractiveQuestions = formData.questions.filter(q => {
        if (q.type === 'interactive-list') {
          const hasValidSections = q.interactive && 
            q.interactive.action && 
            q.interactive.action.sections && 
            q.interactive.action.sections.length > 0 &&
            q.interactive.action.sections.every(section => 
              section.title && section.title.trim().length > 0 &&
              section.rows && section.rows.length > 0 &&
              section.rows.every(row => 
                row.id && row.title && row.title.trim().length > 0
              )
            );
          return !hasValidSections;
        }
        
        if (q.type === 'interactive-button') {
          const hasValidButtons = q.interactive && 
            q.interactive.action && 
            q.interactive.action.buttons && 
            q.interactive.action.buttons.length > 0 &&
            q.interactive.action.buttons.length <= 3 && // WhatsApp limit
            q.interactive.action.buttons.every(button => 
              button.type === 'reply' &&
              button.reply && 
              button.reply.id && 
              button.reply.title && 
              button.reply.title.trim().length > 0 &&
              button.reply.title.trim().length <= 20 // WhatsApp limit
            );
          return !hasValidButtons;
        }
        
        return false;
      });
      
      if (invalidInteractiveQuestions.length > 0) {
        logger.log('❌ useBotForm: Validation failed - invalid interactive questions:', 
          invalidInteractiveQuestions.map(q => ({ id: q.id, type: q.type })));
        return false;
      }
    }
    
    logger.log('✅ useBotForm: Form validation passed');
    return true;
  }, [formData]);

  // 🔥 ENHANCED UPDATE FORM DATA: Helper function
  const updateFormData = useCallback((field: keyof FormData, value: any) => {
    logger.log('📝 useBotForm: Updating form data:', field, value);
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // 🔥 ENHANCED LOAD BOT DATA: ✅ FIXED - Keywords extraction
  useEffect(() => {
    if (!isEdit || !botId || botId === loadedBotId) return;

    const loadBotData = async () => {
      setLoading(true);
      logger.log('📥 useBotForm: Loading bot data for editing:', botId);
      
      try {
        const response = await getBotById(botId);
        const botData = response.data;
        
        if (botData) {
          logger.log('📥 useBotForm: Bot data loaded:', botData);
          
          // ✅ FIXED: Extract keywords from bot data - check multiple possible locations
          let extractedKeywords: string[] = [];
          
          if (botData.keywords && Array.isArray(botData.keywords)) {
            extractedKeywords = botData.keywords;
          } else if (botData.triggerCondition && botData.triggerCondition.keywords && Array.isArray(botData.triggerCondition.keywords)) {
            extractedKeywords = botData.triggerCondition.keywords;
          } else if (botData.groups && botData.groups.length > 0) {
            // Check if keywords are stored in groups structure
            const firstGroup = botData.groups[0];
            if (firstGroup.triggerCondition && firstGroup.triggerCondition.keywords) {
              extractedKeywords = firstGroup.triggerCondition.keywords;
            }
          }
          
          logger.log('🔍 useBotForm: Extracted keywords:', extractedKeywords);
          
          // 🔥 DETECT MODE: Check if this is questionnaire or template bot
          const isQuestionnaireMode = botData.groups?.some((group: any) => 
            group.steps?.some((step: any) => 
              ['text', 'email', 'phone', 'number', 'interactive', 'interactive-list', 'interactive-button', 'location', 'template'].includes(step.fieldType)
            )
          );

          // 🔥 ENHANCED: Convert steps to questions (including interactive types)
          const extractedQuestions: QuestionItem[] = [];
          if (isQuestionnaireMode && botData.groups) {
            botData.groups.forEach((group: any) => {
              group.steps?.forEach((step: any, index: number) => {
                logger.log('🔍 useBotForm: Processing step:', {
                  stepId: step.id,
                  fieldType: step.fieldType,
                  message: step.message,
                  interactive: step.interactive,
                  metadata: step.metadata
                });
                
                // Handle template steps as template questions
                if (step.fieldType === 'template') {
                  logger.log('📥 useBotForm: Processing template step:', {
                    stepId: step.id,
                    stepTemplate: step.template,
                    stepTemplateType: typeof step.template,
                    stepKeys: Object.keys(step)
                  });
                  
                  const templateId = step.template || '';
                  logger.log('📥 useBotForm: Extracted templateId:', templateId);
                  
                  const templateQuestion: QuestionItem = {
                    id: step.id || `template_q_${Date.now()}_${index}`,
                    question: '', // Template questions don't have question text
                    type: 'template',
                    required: false,
                    order: extractedQuestions.length,
                    templateData: {
                      templateId: templateId,
                      templateName: 'Loading template...', // Will be updated after fetch
                      template: null // Will be populated after fetch
                    }
                  };
                  
                  logger.log('📥 useBotForm: Created template question:', {
                    questionId: templateQuestion.id,
                    templateData: templateQuestion.templateData,
                    templateId: templateQuestion.templateData?.templateId
                  });
                  
                  extractedQuestions.push(templateQuestion);
                  return;
                }
                
                // 🔥 ENHANCED: Better type mapping for interactive questions
                let questionType: QuestionItem['type'] = 'text'; // default
                
                if (step.fieldType === 'interactive') {
                  // For interactive type, determine subtype from interactive data
                  if (step.interactive?.type === 'list') {
                    questionType = 'interactive-list';
                  } else if (step.interactive?.type === 'button') {
                    questionType = 'interactive-button';
                  } else {
                    console.warn('⚠️ useBotForm: Unknown interactive type:', step.interactive?.type);
                    questionType = 'interactive-button'; // fallback
                  }
                } else if (['text', 'email', 'phone', 'number', 'location'].includes(step.fieldType)) {
                  questionType = step.fieldType as QuestionItem['type'];
                } else {
                  console.warn('⚠️ useBotForm: Unknown field type, defaulting to text:', step.fieldType);
                  questionType = 'text';
                }
                
                const question: QuestionItem = {
                  id: step.id || `q_${Date.now()}_${index}`,
                  question: step.message || (questionType.startsWith('interactive') ? (step.interactive?.body?.text || '') : ''), // Use interactive body text for interactive questions
                  type: questionType,
                  required: step.validation?.required || false,
                  order: extractedQuestions.length
                };
                
                // 🔥 INTERACTIVE: Extract interactive data if present
                if (step.interactive) {
                  question.interactive = step.interactive;
                  logger.log('📥 useBotForm: Loaded interactive question:', question.id, step.interactive);
                }
                
                
                logger.log('✅ useBotForm: Created question:', {
                  id: question.id,
                  type: question.type,
                  hasInteractive: !!question.interactive,
                  hasTemplateData: !!question.templateData
                });
                
                extractedQuestions.push(question);
              });
            });
          }

          // 🔑 PRESERVE IDS: Extract and store original structural IDs
          const originalIds = {
            groupId: botData.groups?.[0]?.id || undefined,
            templateStepId: botData.groups?.[0]?.steps?.find((step: any) => step.fieldType === 'template')?.id || undefined
          };
          setOriginalBotStructure(originalIds);
          logger.log('🔑 useBotForm: Preserved original IDs:', originalIds);

          // Extract automation data if exists - only actions, no events or criteria
          const existingDayActions = botData.automationRule?.action || { day_1: [] };
          const existingDayDelays = { day_1: 1 }; // Default delays since not in API

          // ✅ FIXED: Update form data with extracted keywords and automation
          setFormData({
            name: botData.name || '',
            description: botData.description || '',
            keywords: extractedKeywords, // ✅ FIXED: Now properly extracted
            selectedTemplate: botData.template || null,
            isActive: botData.isActive !== false,
            responseType: isQuestionnaireMode ? 'questionnaire' : 'template',
            questions: extractedQuestions,
            botType: botData.botType || 'lead_generation',
            dayActions: existingDayActions,
            dayDelays: existingDayDelays
          });

          logger.log('✅ useBotForm: Form data updated with', extractedQuestions.length, 'questions and', extractedKeywords.length, 'keywords');
          logger.log('📊 useBotForm: Question types summary:', extractedQuestions.map(q => ({ id: q.id, type: q.type })));
          
          // 🔥 FETCH TEMPLATE DETAILS: Load template information for template questions
          logger.log('🔍 useBotForm: Checking for template questions to fetch:', extractedQuestions.map(q => ({
            id: q.id,
            type: q.type,
            templateData: q.templateData,
            hasTemplateId: !!q.templateData?.templateId
          })));
          
          const templateQuestions = extractedQuestions.filter(q => q.type === 'template' && q.templateData?.templateId);
          logger.log('📥 useBotForm: Filtered template questions for fetching:', templateQuestions.length);
          
          if (templateQuestions.length > 0) {
            logger.log('📥 useBotForm: Found', templateQuestions.length, 'template questions, fetching template details...');
            
            // Fetch templates in parallel
            const templateFetchPromises = templateQuestions.map(async (question) => {
              try {
                const templateId = question.templateData!.templateId;
                logger.log('📥 useBotForm: Fetching template:', templateId);
                
                const response = await getTemplateById(templateId);
                const templateData = response.data.data || response.data;
                
                if (templateData) {
                  // Update the question with template details
                  question.templateData!.templateName = templateData.name || 'Template';
                  question.templateData!.template = templateData;
                  
                  logger.log('✅ useBotForm: Template details loaded:', templateData.name);
                  return { questionId: question.id, templateData };
                }
                return null;
              } catch (error) {
                console.error('❌ useBotForm: Error fetching template:', question.templateData!.templateId, error);
                // Keep the question but mark template as failed to load
                question.templateData!.templateName = 'Failed to load template';
                return null;
              }
            });
            
            // Wait for all template fetches and then update form data
            try {
              const templateResults = await Promise.all(templateFetchPromises);
              logger.log('✅ useBotForm: All template details loaded, updating form data...');
              
              // Update form data with the enriched questions (create new array to trigger re-render)
              setFormData(prev => ({
                ...prev,
                questions: [...extractedQuestions] // Create new array reference to trigger re-render
              }));
              
              logger.log('✅ useBotForm: Form data updated with template details');
            } catch (error) {
              console.error('❌ useBotForm: Error loading template details:', error);
              toast.error('Some templates failed to load');
            }
          }
          
          setLoadedBotId(botId);
        }
      } catch (error) {
        console.error('❌ useBotForm: Error loading bot data:', error);
        toast.error('Failed to load bot data');
      } finally {
        setLoading(false);
      }
    };

    loadBotData();
  }, [isEdit, botId, loadedBotId]);

  // 🔥 ENHANCED SUBMIT HANDLER: Supports Interactive Questions
 const handleSubmit = useCallback(async (e?: React.FormEvent) => {
  if (e) e.preventDefault();
  
  if (!isFormValid()) {
    logger.log('❌ useBotForm: Cannot submit - form invalid');
    toast.error('Please fill in all required fields');
    return;
  }

  setSaving(true);
  logger.log('💾 useBotForm: Starting form submission...', formData);
  logger.log('💾 useBotForm: Questions details:', formData.questions.map(q => ({
    id: q.id,
    type: q.type,
    templateData: q.templateData,
    hasTemplateId: !!q.templateData?.templateId
  })));

  try {
    // 🔥 ENHANCED: Build bot payload with template question support
    logger.log('💾 useBotForm: FormData before payload creation:', {
      name: formData.name,
      description: formData.description,
      keywords: formData.keywords.length,
      isActive: formData.isActive
    });
    
    const botPayload: any = {
      name: formData.name,
      description: formData.description,
      keywords: formData.keywords,
      template: formData.selectedTemplate,
      isActive: formData.isActive,
      botType: formData.botType,
      triggerCondition: {
        type: 'keyword' as const,
        keywords: formData.keywords,
        requireAllKeywords: formData.keywords.includes('any') ? true : false
      },
      status: formData.isActive ? 'active' as const : 'inactive' as const,
      automationRule: {
        action: formData.dayActions
      },
      groups: [
        {
          id: originalBotStructure.groupId || `group_${Date.now()}`, // 🔑 PRESERVE: Use original group ID if available
          groupName: formData.name,
          required: true,
          conditions: {},
          triggerCondition: { 
            type: 'keyword' as const, 
            keywords: formData.keywords,
            requireAllKeywords: formData.keywords.includes('any') ? true : false
          },
          steps: formData.responseType === 'questionnaire' && formData.questions.length > 0
            ? [
                // Question steps - Enhanced with template question support
                ...formData.questions.map((question, index) => {
                  logger.log('💾 useBotForm: Mapping question to step:', {
                    index,
                    questionId: question.id,
                    type: question.type,
                    templateData: question.templateData
                  });
                  
                  // 🔥 TEMPLATE QUESTION: Special handling for template type
                  if (question.type === 'template') {
                    logger.log('💾 useBotForm: Processing template question:', {
                      questionId: question.id,
                      templateData: question.templateData,
                      templateId: question.templateData?.templateId
                    });
                    
                    const templateId = question.templateData?.templateId || '';
                    if (!templateId) {
                      console.warn('⚠️ useBotForm: Template question missing templateId:', question);
                    }
                    
                    return {
                      id: question.id,
                      fieldType: 'template',
                      template: templateId
                    };
                  }
                  
                  // 🔥 REGULAR QUESTIONS: Existing logic for other question types
                  const baseStep: any = {
                    id: question.id,
                    fieldType: question.type === 'interactive-list' ? 'interactive' 
                             : question.type === 'interactive-button' ? 'interactive'
                             : question.type,
                    message: question.question || '', // 🔥 FIX: Always use user's question text, even for interactive
                    validation: { 
                      required: question.required
                    },
                    metadata: {
                      ...(getHardcodedCrmField(question.type, question.id) && {
                        crm_field: getHardcodedCrmField(question.type, question.id)
                      })
                    },
                    options: {}
                  };

                  // Add interactive data if present
                  if (question.interactive) {
                    // 🔥 FIX: Ensure interactive body.text matches user's question input
                    const interactiveWithUserText = {
                      ...question.interactive,
                      body: {
                        text: question.question || question.interactive.body?.text || 'Please select an option'
                      }
                    };
                    baseStep.interactive = interactiveWithUserText;
                  }

                  return baseStep;
                })
              ]
            : [
                // Template-only mode
                {
                  id: originalBotStructure.templateStepId || `template_${Date.now()}`, // 🔑 PRESERVE: Use original template step ID
                  fieldType: 'template',
                  template: formData.selectedTemplate?.id || formData.selectedTemplate?._id || ''
                }
              ]
        }
      ]
    };

    logger.log('💾 useBotForm: Final payload with template questions:', botPayload);
    logger.log('💾 useBotForm: Payload description field specifically:', JSON.stringify(botPayload.description));

    // API call (unchanged)
    const response = isEdit 
      ? await updateBot(botId!, botPayload)
      : await createBot(botPayload);

    logger.log('✅ useBotForm: Bot saved successfully:', response);
    toast.success(isEdit ? 'Bot updated successfully!' : 'Bot created successfully!');
    
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    console.error('❌ useBotForm: Error saving bot:', error);
    toast.error('Failed to save bot. Please try again.');
  } finally {
    setSaving(false);
  }
}, [formData, isEdit, botId, isFormValid, onSuccess]);

  // 🔄 FORM STATE CHANGE CALLBACK: Notify parent component of form state changes
  useEffect(() => {
    if (onFormStateChange) {
      const formValid = isFormValid();
      logger.log('📡 useBotForm: Calling onFormStateChange:', { formValid, saving });
      onFormStateChange(formValid, saving);
    }
  }, [formData, saving, isFormValid, onFormStateChange]);

  // 🔥 RETURN: Enhanced interface
  return {
    formData,
    setFormData,
    updateFormData,
    isFormValid: isFormValid(),
    loading,
    saving,
    handleSubmit
  };
};